import React from "react";
import { Check, Phone, Play } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const DemoPill = ({ agent, index, onDemo }) => {
  const call = !!agent.demo;
  return (
    <a
      href={call ? `tel:${agent.demo.tel}` : `#demos-${index}-play`}
      onClick={call ? undefined : onDemo}
      data-testid={call ? "agent-demo-call" : "agent-demo-see"}
      className="group inline-flex items-center gap-3 rounded-full pl-2 pr-5 py-2 shadow-[0_0_0_4px_rgba(232,206,149,0.18),0_12px_32px_-8px_rgba(232,206,149,0.65)] hover:-translate-y-0.5 transition-transform"
      style={{ background: "linear-gradient(135deg, #F3DFAE 0%, #E8CE95 45%, #C79B55 100%)" }}
    >
      <span className="relative w-9 h-9 rounded-full flex items-center justify-center bg-[var(--night)]">
        <span className="absolute inset-0 rounded-full animate-ping opacity-30 bg-[var(--night)]" />
        {call ? (
          <Phone className="relative w-4 h-4 text-[#E8CE95]" strokeWidth={2} />
        ) : (
          <Play className="relative w-4 h-4 text-[#E8CE95] ml-0.5" strokeWidth={2} />
        )}
      </span>
      <span className="leading-tight">
        {call && (
          <span className="block font-mono text-[9.5px] tracking-[0.2em] uppercase font-semibold text-[#5C4530]">{agent.demo.label}</span>
        )}
        <span className="block text-[16px] font-semibold text-[var(--night)]">
          {call ? agent.demo.number : "Click demo"}
        </span>
      </span>
    </a>
  );
};

const SectionHead = ({ children }) => (
  <h3 className="font-mono text-[10px] tracking-[0.22em] uppercase font-bold text-[var(--night)] mb-2.5">{children}</h3>
);

const BuildPlan = ({ build }) => (
  <section data-testid="agent-build-plan" className="mt-6">
    <SectionHead>How we work with you</SectionHead>
    <div className="grid gap-px bg-[var(--line)] rounded-xl overflow-hidden border border-[var(--line)]">
      {build.phases.map((p, i) => (
        <div key={i} className={`px-4 py-2 flex gap-4 items-baseline ${p.t === "Ongoing Support" ? "bg-[#FBF5E8]" : "bg-white"}`}>
          <span className="shrink-0 w-[84px] font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--blue)]">
            {p.t}
            {p.t === "Ongoing Support" && (
              <span className="block mt-1 normal-case tracking-normal font-sans text-[10px] font-medium text-[#8A6A3E]">Every month</span>
            )}
          </span>
          <span className="text-[13.5px] leading-[1.45] text-[var(--ink-soft)]">{p.d}</span>
        </div>
      ))}
    </div>
  </section>
);

export const AgentDialog = ({ agent, index, onClose }) => (
  <Dialog open={!!agent} onOpenChange={(o) => !o && onClose()}>
    <DialogContent
      data-testid="agent-dialog"
      className="max-w-[640px] w-[94vw] max-h-[88vh] overflow-y-auto rounded-2xl border border-[var(--line)] bg-white p-0 [&>button]:text-white [&>button]:opacity-90 [&>button]:z-10"
    >
      {agent && (
        <div>
          <header className="relative bg-[var(--night)] text-white px-6 sm:px-8 py-4 overflow-hidden">
            <div className="absolute -top-24 -right-20 w-[260px] h-[260px] orb opacity-70" />
            <div className="relative flex flex-wrap items-center justify-between gap-3 pr-8">
              <DialogTitle data-testid="agent-dialog-title" className="font-serif font-medium text-[26px] sm:text-[30px] leading-tight text-white tracking-normal">
                {agent.name}
              </DialogTitle>
              <DemoPill agent={agent} index={index} onDemo={onClose} />
            </div>
            <DialogDescription className="sr-only">
              {agent.desc}
            </DialogDescription>
          </header>

          <div className="px-6 sm:px-8 py-5">
            <section data-testid="agent-how-it-works">
            <SectionHead>How it works</SectionHead>
            <ol className="space-y-2">
              {agent.steps.map((s, i) => (
                <li key={i} className="flex gap-4 items-start">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-[var(--blue-tint)] border border-[var(--blue)]/20 text-[var(--blue)] font-mono text-[11px] flex items-center justify-center">
                    {i + 1}
                  </span>
                  <p className="text-[14px] leading-[1.5] text-[var(--ink-soft)] pt-0.5">{s}</p>
                </li>
              ))}
            </ol>
            </section>

            <section data-testid="agent-benefits" className="mt-6">
            <SectionHead>Benefits</SectionHead>
            <div className="flex flex-wrap items-center gap-2">
              {agent.gets.map((g, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--porcelain-mist)] border border-[var(--line)] px-3 py-1 text-[13px] text-[var(--ink)]">
                  <Check className="w-3.5 h-3.5 text-[var(--blue)]" strokeWidth={2.5} />
                  {g}
                </span>
              ))}
            </div>
            </section>

            {agent.build && <BuildPlan build={agent.build} />}

          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);
